import React from "react"
import { Dropdown } from "antd"
import type { MenuProps } from "antd"
import { css, useTheme } from "@emotion/react"
import { ExternalLinkWrapper } from "../../ExternalLinkWrapper"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro"
import { BodyTextRegular, BodyTextSemibold } from "../../typography/text"
import { Divider } from "../../dividers/Divider"
import { ButtonPrimary } from "../../button/buttons"
import { Flex } from "rebass"
import { useTranslation } from "react-i18next"
import { translationKeys } from "../../../util/translationKeys"
import { useUser } from "../../../services/queries"
import { useAccount } from "../../../context/AccountContext"
import { capitalize } from "../../../util/util"

const linkStyle = css`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  padding-top: 8px;
  padding-bottom: 8px;
  padding-left: 4px;
  padding-right: 4px;
`

const buttonStyle = css`
  padding-top: 6px;
  padding-bottom: 6px;
  padding-left: 8px;
  padding-right: 8px;
`

const SUPPORT_CHAT = process.env.GATSBY_SUPPORT_CHAT || ""
const SUPPORT_EMAIL = process.env.GATSBY_SUPPORT_EMAIL || ""
const SUPPORT_WEB = process.env.GATSBY_SUPPORT_WEB || ""

export const UserDropdown = () => {
  const theme = useTheme()
  const { t } = useTranslation()

  const { signoutAndClearCaches } = useAccount()

  const { data: user, isLoading: userLoading } = useUser()

  const dropdownWrapper = css`
    flex-direction: column;
    gap: 8px;
    background-color: ${theme.colors.white};
    border-radius: 4px;
    padding: 8px;
    box-shadow: 4px 4px 16px 8px rgba(0, 0, 0, 0.1);
  `

  const userNameContainerStyle = css`
    flex-direction: row;
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    color: ${theme.colors.primary};
    &:hover {
      color: ${theme.colors.ctaMain};
    }
  `

  const items: MenuProps["items"] = [
    ...(SUPPORT_CHAT
      ? [
          {
            key: "1",
            label: (
              <ExternalLinkWrapper href={SUPPORT_CHAT} css={linkStyle}>
                <FontAwesomeIcon icon={solid("message-dots")} />
                <BodyTextRegular color={theme.colors.primary}>
                  {t(translationKeys.comboProfileSupport)}
                </BodyTextRegular>
              </ExternalLinkWrapper>
            ),
          },
        ]
      : []),
    ...(SUPPORT_EMAIL
      ? [
          {
            key: "2",
            label: (
              <ExternalLinkWrapper
                href={`mailto:${SUPPORT_EMAIL}`}
                css={linkStyle}
              >
                <FontAwesomeIcon icon={solid("envelope")} />
                <BodyTextRegular color={theme.colors.primary}>
                  {t(translationKeys.comboProfileSupportEmail)}
                </BodyTextRegular>
              </ExternalLinkWrapper>
            ),
          },
        ]
      : []),
    ...(SUPPORT_WEB
      ? [
          {
            key: "3",
            label: (
              <ExternalLinkWrapper href={SUPPORT_WEB} css={linkStyle}>
                <FontAwesomeIcon icon={solid("message")} />
                <BodyTextRegular color={theme.colors.primary}>
                  {t(translationKeys.comboProfileSupportWeb)}
                </BodyTextRegular>
              </ExternalLinkWrapper>
            ),
          },
        ]
      : []),
  ]

  const menuStyle = {
    boxShadow: "none",
    padding: "0px",
  }

  if (!user || userLoading) {
    return null
  }

  return (
    <Dropdown
      menu={{ items }}
      placement="bottomRight"
      dropdownRender={(menu) => (
        <Flex css={dropdownWrapper}>
          {React.cloneElement(menu as React.ReactElement, { style: menuStyle })}
          <Divider />
          <ButtonPrimary
            type="button"
            css={buttonStyle}
            backgroundColor={theme.colors.white}
            hoverColor={theme.colors.errorLight}
            color={theme.colors.errorDark}
            onClick={signoutAndClearCaches}
          >
            {t(translationKeys.comboProfileSignout)}
          </ButtonPrimary>
        </Flex>
      )}
    >
      <div css={userNameContainerStyle}>
        <BodyTextSemibold>
          {capitalize(user.first_name || "")}{" "}
          {capitalize(user.last_name?.charAt(0) || "")}
        </BodyTextSemibold>
        <FontAwesomeIcon icon={solid("chevron-down")} />
      </div>
    </Dropdown>
  )
}
